import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Avatar,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  AttachMoney as PriceIcon,
} from '@mui/icons-material';
import { Service, Category } from '../../types';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../../hooks/useLocalizedField';

interface ServiceSelectionProps {
  services: Service[];
  categories: Category[];
  selectedService: Service | null;
  onSelectService: (service: Service) => void;
}

const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  services,
  categories,
  selectedService,
  onSelectService
}) => {
  const { t } = useTranslation();
  const { getField } = useLocalizedField();
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const activeServices = services;

  const categoryKeys = Array.from(new Set(activeServices.map(s => s.category))).sort((a, b) => {
    const catA = categories.find(c => c.name === a);
    const catB = categories.find(c => c.name === b);
    if (catA && catB) return 0;
    return a.localeCompare(b);
  });

  const getCategoryLabel = (name: string) => {
    const cat = categories.find(c => c.name === name);
    return cat ? getField(cat, 'name', cat.name) : name;
  };

  const filteredServices = activeCategory === 'all'
    ? activeServices
    : activeServices.filter(s => s.category === activeCategory);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('serviceSelection.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('serviceSelection.subtitle')}
      </Typography>

      {/* Category filter chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Chip
          label={t('serviceSelection.all')}
          onClick={() => setActiveCategory('all')}
          color={activeCategory === 'all' ? 'primary' : 'default'}
          variant={activeCategory === 'all' ? 'filled' : 'outlined'}
          sx={{ fontWeight: activeCategory === 'all' ? 600 : 400 }}
        />
        {categoryKeys.map(cat => (
          <Chip
            key={cat}
            label={getCategoryLabel(cat)}
            onClick={() => setActiveCategory(cat)}
            color={activeCategory === cat ? 'primary' : 'default'}
            variant={activeCategory === cat ? 'filled' : 'outlined'}
            sx={{ fontWeight: activeCategory === cat ? 600 : 400 }}
          />
        ))}
      </Box>

      {/* Service grid */}
      <Grid container spacing={2}>
        {filteredServices.map((service) => {
          const isSelected = selectedService?.id === service.id;
          return (
            <Grid size={{ xs: 12, sm: 6 }} key={service.id}>
              <Card
                onClick={() => onSelectService(service)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  borderRadius: 3,
                  boxShadow: isSelected
                    ? '0 0 0 1px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.12)'
                    : '0 1px 4px rgba(0,0,0,0.08)',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Selected badge */}
                {isSelected && (
                  <Avatar
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      width: 28,
                      height: 28,
                      bgcolor: 'primary.main',
                      zIndex: 1,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                )}

                <Box sx={{ display: 'flex', height: 120 }}>
                  {service.imageUrl ? (
                    <CardMedia
                      component="img"
                      sx={{ width: 120, flexShrink: 0, objectFit: 'cover' }}
                      image={service.imageUrl}
                      alt={service.name}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 120,
                        flexShrink: 0,
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h4" sx={{ opacity: 0.4 }}>✂️</Typography>
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      flex: 1,
                      p: 1.5,
                      pb: '12px !important',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                          lineHeight: 1.3,
                          mb: 0.5,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {getField(service, 'name', service.name)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                        }}
                      >
                        {getField(service, 'description', service.description)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {service.showPrice !== false && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <PriceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" fontWeight={600} color="text.primary">
                            {service.price.min === service.price.max
                              ? `$${service.price.min}`
                              : `$${service.price.min}–$${service.price.max}`}
                          </Typography>
                        </Box>
                      )}
                      {service.showPrice !== false && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {service.duration} {t('common.min')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredServices.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="body1">{t('serviceSelection.noServices') || 'Aucun service disponible'}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceSelection;
